import { useEffect, useSyncExternalStore } from "react";
import { authorizeRoute } from "@carepass/auth";
import { Button } from "@carepass/ui";
import { AuthPage } from "../features/auth/AuthPages";
import { MemberPage } from "../features/member/MemberPage";
import { PartnerJoinPage } from "../features/partner-join/PartnerJoinPage";
import { PublicPage } from "../features/public/PublicPages";
import { DirectSignupPage } from "../features/signup/DirectSignupPage";
import { EmptyState, navigate } from "../features/shared/ui";
import { MemberScreenFrame } from "../features/shared/MemberLayouts";
import { MemberTopBar } from "../features/member/MemberMobileUi";
import { memberQueryClient } from "./providers";
import { memberRouter, memberSessionStore } from "./runtime";
import { isPublicPrivacyRequest, publicPrivacyBackTo } from "../features/public/public-document";

const subscribeSession = (listener: () => void) => memberSessionStore.subscribe(() => listener());
const getSession = () => memberSessionStore.get();

function LoginRequired() {
  const current = `${memberRouter.getSnapshot().location.pathname}${memberRouter.getSnapshot().location.search}`;
  return (
    <EmptyState
      title="로그인이 필요합니다"
      description="로그인하면 확인하던 화면으로 돌아옵니다. 처음 이용하신다면 회원가입을 진행해 주세요."
      action={<div className="empty-state__actions"><Button onClick={() => navigate(`/login?returnTo=${encodeURIComponent(current)}`)}>로그인</Button><Button variant="secondary" onClick={() => navigate("/signup")}>회원가입</Button>{memberRouter.getSnapshot().location.pathname === "/app/my/termination" && <Button variant="quiet" onClick={() => navigate("/termination/result")}>해지 결과 조회</Button>}</div>}
    />
  );
}

export function App() {
  const snapshot = useSyncExternalStore(memberRouter.subscribe, memberRouter.getSnapshot, memberRouter.getSnapshot);
  const session = useSyncExternalStore(subscribeSession, getSession, () => null);
  const route = snapshot.route.metadata;
  const access = authorizeRoute(route.access, session);

  useEffect(() => {
    if (!session) memberQueryClient.removeQueries({ queryKey: ["member"] });
  }, [session]);

  if (access === "unauthenticated") return <div className="app-shell app-shell--auth"><main className="standalone-content" id="main-content"><MemberTopBar title="로그인 안내" backTo="/"/><div className="auth-page member-identity-card member-access-message"><LoginRequired/></div></main></div>;

  if (route.surface === "partner-join") return <PartnerJoinPage />;
  if (route.surface === "signup") return <DirectSignupPage />;

  let content;
  if (!snapshot.found) {
    content = <EmptyState title="페이지를 찾을 수 없습니다" description="주소를 확인하거나 홈으로 돌아가 주세요." action={<Button onClick={() => navigate(session ? "/app" : "/")}>홈으로</Button>} />;

  } else if (route.surface === "public") {
    content = <PublicPage route={route} params={snapshot.params} />;
  } else if (route.surface === "auth") {
    content = <AuthPage route={route} />;
  } else {
    content = <MemberPage route={route} params={snapshot.params} />;
  }

  const memberSurface = route.surface === "member" && access === "allowed";
  if (memberSurface) return <MemberScreenFrame pathname={snapshot.location.pathname}>{content}</MemberScreenFrame>;
  const standaloneSurface = (route.id === "PUB-S01" || route.surface === "auth") && snapshot.found;
  const publicReadingSurface = route.surface === "public" && route.id !== "PUB-S01" && snapshot.found;
  const publicPrivacySurface = route.id === "PUB-S09-LATEST" && isPublicPrivacyRequest(snapshot.location.search);
  const publicReadingTitle = publicPrivacySurface ? "개인정보처리방침" : route.title;
  const publicReadingBackTo = route.id === "PUB-S06-DETAIL" ? "/notices" : session ? "/app/my" : "/";
  return (
    <div className={`app-shell app-shell--${route.surface}`}>
      <a className="skip-link" href="#main-content">본문으로 바로가기</a>
      {!snapshot.found && <MemberTopBar title="페이지 안내" backTo={session ? "/app" : "/"} />}
      <main className={standaloneSurface ? "standalone-content" : memberSurface ? "member-content" : publicReadingSurface ? "public-reading-content" : "public-reading-content"} id="main-content">{publicReadingSurface ? <><MemberTopBar title={publicReadingTitle} backTo={publicPrivacySurface ? publicPrivacyBackTo(snapshot.location.search, Boolean(session), publicReadingBackTo) : publicReadingBackTo} /><div className="public-reading-body">{content}</div></> : content}</main>
    </div>
  );
}
